/**
 * Logo als vectorpaden voor het rapport (geen raster): de SVG uit `brand/` wordt omgezet in
 * twee padstrings (woordmerk + oranje delen) die pdf-lib met `drawSvgPath` tekent.
 */
export interface LogoPaths {
  /** Woordmerk en schild (wit of zwart, afhankelijk van de variant). */
  word: string;
  /** Oranje delen. */
  accent: string;
  /**
   * Beeldmerk met eigen schaal (alternatief logo op wit): wit schild, rand in de kleur van het
   * woordmerk, oranje vlakken. Coördinaten binnen de groep, met `tx/ty/s` naar de logo-viewBox.
   */
  emblem?: { tx: number; ty: number; s: number; shield: string; rim: string; accent: string };
  /** Uitsnede binnen de viewBox; standaard die van het Pandwachters-logo. */
  box?: { x: number; y: number; w: number; h: number };
}

/** Uitsnede rond het logo binnen de viewBox (zonder de witruimte eromheen). */
export const LOGO_BOX = { x: 100.4, y: 86.8, w: 836.2, h: 256.6 } as const;

const attr = (el: string, name: string) => new RegExp(`\\s${name}="([^"]*)"`).exec(el)?.[1] ?? "";

function pointsToPath(points: string): string {
  const n = points
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  let d = "";
  for (let k = 0; k + 1 < n.length; k += 2) d += `${k ? "L" : "M"}${n[k]} ${n[k + 1]}`;
  return d + "Z";
}

export function parseLogoSvg(svg: string): LogoPaths {
  const out: LogoPaths = { word: "", accent: "" };
  for (const m of svg.matchAll(/<(path|polygon|rect)\b[^>]*>/g)) {
    const el = m[0];
    let d = "";
    if (m[1] === "path") d = attr(el, "d");
    else if (m[1] === "polygon") d = pointsToPath(attr(el, "points"));
    else {
      const n = (a: string) => Number(attr(el, a) || 0);
      d = `M${n("x")} ${n("y")}h${n("width")}v${n("height")}h${-n("width")}Z`;
    }
    if (attr(el, "class") === "cls-2") out.accent += d;
    else out.word += d;
  }
  return out;
}

/** Paden (path/polygon/rect/circle) in een stuk SVG, samengevoegd tot één padstring. */
function shapes(svg: string): string {
  let d = "";
  for (const m of svg.matchAll(/<(path|polygon|rect|circle)\b[^>]*>/g)) {
    const el = m[0];
    if (m[1] === "path") d += attr(el, "d");
    else if (m[1] === "circle") {
      const [cx, cy, r] = ["cx", "cy", "r"].map((a) => Number(attr(el, a) || 0)) as [number, number, number];
      d += `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
    } else if (m[1] === "polygon") d += pointsToPath(attr(el, "points"));
    else {
      const n = (a: string) => Number(attr(el, a) || 0);
      d += `M${n("x")} ${n("y")}h${n("width")}v${n("height")}h${-n("width")}Z`;
    }
  }
  return d;
}

/**
 * Alternatief logo op wit (`brand/logo-alternatief-op-wit.svg`): beeldmerk in een groep met
 * translate/scale (wit schild, zwarte rand, oranje vlakken), woordmerk in een zwarte groep,
 * oranje punt los.
 */
export function parseAltLogoSvg(svg: string): LogoPaths {
  const group = /<g transform="translate\(([\d.-]+)[ ,]+([\d.-]+)\) scale\(([\d.]+)\)">([\s\S]*?)<\/g>\s*<\/g>/.exec(
    svg,
  );
  if (!group) throw new Error("beeldmerk niet gevonden");
  const [, tx, ty, sc, inner] = group as unknown as [string, string, string, string, string];
  const shield = /<polygon fill="#fff"[^>]*>/.exec(inner)?.[0] ?? "";
  const rim = /<path fill="#000"[^>]*>/.exec(inner)?.[0] ?? "";
  const emblemAccent = /<g fill="#ec663a">([\s\S]*)$/.exec(inner)?.[1] ?? "";
  const rest = svg.slice(group.index + group[0].length);
  const word = /<g fill="#000">([\s\S]*?)<\/g>/.exec(rest)?.[1] ?? "";
  const dot = [...rest.matchAll(/<path fill="#ec663a"[^>]*>/g)].map((m) => m[0]).join("");
  return {
    word: shapes(word),
    accent: shapes(dot),
    emblem: {
      tx: Number(tx),
      ty: Number(ty),
      s: Number(sc),
      shield: shapes(shield),
      rim: shapes(rim),
      accent: shapes(emblemAccent),
    },
  };
}

/**
 * Klantpunt-logo (`brand/klantpunt/logo.svg`): beeldmerk in `kp-mark` (accentkleur), woordmerk in
 * `kp-word`; uitsnede uit de viewBox.
 */
export function parseKlantpuntLogoSvg(svg: string): LogoPaths {
  const vb = /viewBox="([\d.\s-]+)"/.exec(svg)?.[1]?.trim().split(/\s+/).map(Number) ?? [0, 0, 1, 1];
  const group = (cls: string) => new RegExp(`<g class="${cls}"[^>]*>([\\s\\S]*?)</g>`).exec(svg)?.[1] ?? "";
  return {
    word: shapes(group("kp-word")),
    accent: shapes(group("kp-mark")),
    box: { x: vb[0]!, y: vb[1]!, w: vb[2]!, h: vb[3]! },
  };
}
